import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ProgressBar from "components/ProgressBar";
import Button from "components/Button";
import Dropdown from "components/Dropdown";
import ModalGeneralFailed from "screens/ModalGeneralFailed";
import { useQuery, useMutation } from "@apollo/client";
import { GET_ALL_AVAILABLE_KIOSKS, PASSPORT_SCAN } from "graphql/service";
import { Head, Container, StateContainer, Content, Footer } from "./styles";
import { PassportScannerIcon } from "assets/icons";
import { debouncedErrorMessage } from "utils/messageUtil";

const ConfirmIdentity = ({status}) => {
  const navigate = useNavigate();
  const groupId = useParams().groupId;

  const [method, setMethod] = useState("");
  const [usePassportScanner, setUsePassportScanner] = useState(false);
  const [modalFailedIsOpen, setModalFailedIsOpen] = useState(false);

  const { data, error } = useQuery(GET_ALL_AVAILABLE_KIOSKS);

  const options = useMemo(() => {
    // Map available kiosks if they exist
    const kioskOptions = data?.availableKiosks?.map((kiosk) => ({
      label: kiosk.username,
      value: kiosk.kioskId,
    })) ?? [];

    // Always append Manual Check at the end
    return [...kioskOptions, { label: "Manual Check", value: "Manual Check" }];
  }, [data]);

  useEffect(() => {
    if (!error) return;

    if (!error.networkError) {
      let content = error.message;

      if (error.message.includes('Validation')) {
        content = JSON.stringify(
          error.graphQLErrors[0]?.extensions?.exception
        );
      }

      debouncedErrorMessage(content);
    }
  }, [error]);

  const [passportScan, { loading: passportScanLoading }] = useMutation(
    PASSPORT_SCAN,
    {
      onCompleted: (scanData) => {
        if (scanData) {
          setUsePassportScanner(false);
          navigate(
            `/group-check-in/customer-identification/${groupId}`,
            {
              state: {
                scanResult: scanData.scan
              }
            }
          );
        } else {
          setModalFailedIsOpen(true);
        }
      },
      onError: (error) => {
        setModalFailedIsOpen(true);
      },
    }
  );

  const handleSend = () => {
    if (passportScanLoading) return;
    if (method === "") {
      const content = "Please select a method";
      debouncedErrorMessage(content);
    } else if (method === "Manual Check") {
      navigate(`/group-check-in/customer-identification/${groupId}`);
    } else {
      setUsePassportScanner(true);
    }
  };

  const handleScan = () => {
    if (passportScanLoading) return;
    passportScan({
      variables: {
        input: {
          groupId: groupId,
          deviceId: "b74339ea-80a8-4aa5-bd90-0aed6e69273f", //TODO: change to real choice of device
        }
      }
    });
  };

  return (
    <Container>
      <ProgressBar step={2} groupId={groupId} status={status}/>
      {usePassportScanner ? (
        <>
          <StateContainer>
            <div className="instruction">
              <div className="icon">
                <PassportScannerIcon />
              </div>
              <div className="title">
                <div>{`Place customers documents on reader at ${options.find((option) => option.value === method)?.label}.`}</div>
              </div>
            </div>
          </StateContainer>
          <Footer>
            <div className="buttonContainer">
              <Button
                mainPage
                success
                onClick={handleScan}
                data-loading={passportScanLoading}
                additionalstyle={`
                  width: 66px;
                `}
              >
                {passportScanLoading ? null : "Scan"}
              </Button>
            </div>
          </Footer>
        </>
      ) : (
        <>
          <Content>
            <Head>Confirm Identity</Head>
            <div className="text">
              <div>Which identity scanner device would you like to use?</div>
            </div>
            <Dropdown
              mainPage
              options={options}
              value={
                options.find((option) => option.value === method)?.label ||
                "- Select -"
              }
              title="PREFERRED METHOD"
              onChange={(e) => {
                setMethod(e);
              }}
            />
          </Content>
          <Footer>
            <div className="buttonContainer">
              <Button
                mainPage
                success
                onClick={handleSend}
                additionalstyle={`
                  width: 68px;
                `}
              >
                Send
              </Button>
            </div>
          </Footer>
        </>
      )}
      <ModalGeneralFailed
        isOpen={modalFailedIsOpen}
        setIsOpen={setModalFailedIsOpen}
        type="passportScan"
        loading={passportScanLoading}
        retryFunction={handleScan}
      />
    </Container>
  );
};

export default ConfirmIdentity;
